import React from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "./utils";
import { base44 } from "./api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { 
  Home, Building2, TrendingUp, Hammer, 
  Users, BookOpen, Phone, LayoutDashboard,
  MapPin, Mail, Facebook, Linkedin, Instagram,
  Menu, X, Crown 
} from "lucide-react";

const navigationItems = [
  { title: "Accueil", url: "/accueil", icon: Home },
  { title: "Projets", url: "/projets", icon: Building2 },
  { title: "Préfinancements", url: "/prefinancements", icon: TrendingUp },
  { title: "Services", url: "/services", icon: Hammer },
  { title: "À propos", url: "/apropos", icon: Users },
  { title: "Blog", url: "/blog", icon: BookOpen },
  { title: "Contact", url: "/contact", icon: Phone },
];

export default function Layout({ children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showInitAdminBanner, setShowInitAdminBanner] = React.useState(false);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        const allSettings = await base44.entities.Settings.list();
        return allSettings.length > 0 ? allSettings[0] : null;
      } catch { return null; }
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  const { data: user } = useQuery({
    queryKey: ['current-user-layout'],
    queryFn: async () => {
      try { return await base44.auth.me(); } catch { return null; }
    },
    retry: false
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['all-users-banner'],
    queryFn: async () => {
      try { return await base44.entities.User.list(); } catch { return []; }
    },
    enabled: !!user,
    retry: false
  });

  React.useEffect(() => {
    if (user && allUsers.length > 0) {
      const principalExists = allUsers.some(u => u.admin_role === 'principal');
      setShowInitAdminBanner(!principalExists);
    } else {
      setShowInitAdminBanner(false);
    }
  }, [user, allUsers]);

  const isAdmin = user?.admin_role === 'principal' || user?.admin_role === 'secondaire';
  const defaultSettings = {
    company_name: 'R2B Entreprises',
    address: "Abidjan, Côte d'Ivoire",
    phone: '+225 0585868767',
    email: 'contact@r2bentreprises.com',
    footer_text: "L'immobilier participatif, transparent et accessible à tous.",
    facebook_url: '', instagram_url: '', linkedin_url: ''
  };
  const s = settings || defaultSettings;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <style>{`
        :root { --primary:#000000; --accent:#c41e3a; --accent-light:#e63950; }
        .gradient-bg { background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%); }
        .accent-gradient { background: linear-gradient(135deg, #c41e3a 0%, #e63950 100%); }
      `}</style>

      {showInitAdminBanner && user && location.pathname !== '/initadmin' && (
        <div className="bg-gradient-to-r from-[#c41e3a] to-[#a61c3c] text-white py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5" />
              <p className="text-sm font-medium">Bienvenue ! Initialisez votre compte administrateur.</p>
            </div>
            <Link to="/initadmin">
              <button className="bg-white text-[#c41e3a] px-4 py-1 rounded-lg text-sm font-bold hover:bg-gray-100">
                Initialiser mon compte
              </button>
            </Link>
          </div>
        </div>
      )}

      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <Link to="/accueil" className="flex items-center gap-3">
              <img 
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68ea1fd8027db0f0dae6bce0/40ee4795e_Marketing_Strategies_for_Your_Start-up__-_24-removebg-preview.png" 
                alt="R2B Entreprises" className="h-28 w-auto"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navigationItems.map((item) => (
                <Link key={item.title} to={item.url}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm font-medium ${
                    location.pathname === item.url ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  <item.icon className="w-4 h-4" />
                  <span>{item.title}</span>
                </Link>
              ))}
              {isAdmin && (
                <Link to="/dashboard" className="ml-2 px-4 py-2 accent-gradient text-white rounded-lg hover:opacity-90 font-medium text-sm flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />Admin
                </Link>
              )}
            </nav>

            <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t">
              <nav className="flex flex-col gap-2">
                {navigationItems.map((item) => (
                  <Link key={item.title} to={item.url}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                      location.pathname === item.url ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}>
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.title}</span>
                  </Link>
                ))}
                {isAdmin && (
                  <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 mt-2 accent-gradient text-white rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    <LayoutDashboard className="w-5 h-5" />
                    <span className="font-medium">Dashboard Admin</span>
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-white border-t-4 border-[#c41e3a] mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68ea1fd8027db0f0dae6bce0/40ee4795e_Marketing_Strategies_for_Your_Start-up__-_24-removebg-preview.png" alt="R2B" className="h-20 w-auto mb-4" />
              <p className="text-gray-600 text-sm">{s.footer_text}</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-black">Liens Rapides</h4>
              <ul className="space-y-2">
                {[["Nos Projets","/projets"],["Préfinancements","/prefinancements"],["Services","/services"],["À Propos","/apropos"]].map(([label,url]) => (
                  <li key={url}><Link to={url} className="text-gray-600 hover:text-[#c41e3a] transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-black">Contact</h4>
              <ul className="space-y-3 text-gray-600 text-sm">
                <li className="flex items-start gap-2"><MapPin className="w-5 h-5 text-[#c41e3a] mt-0.5" /><span>{s.address}</span></li>
                <li className="flex items-center gap-2"><Phone className="w-5 h-5 text-[#c41e3a]" /><span>{s.phone}</span></li>
                <li className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#c41e3a]" /><span>{s.email}</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-black">Suivez-nous</h4>
              <div className="flex gap-3 mb-6">
                {s.facebook_url && <a href={s.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#c41e3a] hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all"><Facebook className="w-5 h-5" /></a>}
                {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#c41e3a] hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all"><Instagram className="w-5 h-5" /></a>}
                {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-[#c41e3a] hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all"><Linkedin className="w-5 h-5" /></a>}
              </div>
              <Link to="/contact" className="inline-block px-6 py-2 accent-gradient text-white rounded-lg hover:opacity-90 font-medium text-sm">Nous Contacter</Link>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-6 flex flex-col items-center text-center text-gray-600 text-sm">
            <p>© {new Date().getFullYear()} {s.company_name}. Tous droits réservés.</p>
            {(settings?.company_legal_name || settings?.company_registration_number) && (
              <div className="text-xs text-gray-500 mt-2 space-y-1">
                {settings?.company_legal_name && <p><strong>Raison sociale :</strong> {settings.company_legal_name}</p>}
                {settings?.company_registration_number && <p><strong>RCCM :</strong> {settings.company_registration_number}</p>}
                {settings?.company_tax_id && <p><strong>NIF :</strong> {settings.company_tax_id}</p>}
                {settings?.company_legal_form && <p><strong>Forme juridique :</strong> {settings.company_legal_form}</p>}
              </div>
            )}
            <div className="mt-4">
              <Link to="/login" className="text-gray-400 hover:text-gray-600 transition-colors text-xs">
                Connexion Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
